import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function Garantia() {
  const [tab, setTab] = useState('nuevo');
  const [warehouses, setWarehouses] = useState([]);
  const [disponible, setDisponible] = useState([]);
  const [garantias, setGarantias] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [responsable, setResponsable] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get('/catalogs/warehouses'),
      API.get('/garantia')
    ]).then(([w, gar]) => { setWarehouses(w.data); setGarantias(gar.data); });
  }, []);

  useEffect(() => {
    if (warehouseId) {
      API.get('/garantia/disponible', { params: { warehouse_id: warehouseId } }).then(res => setDisponible(res.data));
    }
  }, [warehouseId]);

  const dispGrupo = disponible.find(d => d.group_code === groupCode)?.disponible || 0;

  const handleSave = async () => {
    if (!warehouseId || !groupCode || !cantidad || !responsable) {
      toast.error('Selecciona almacén, grupo, cantidad y responsable'); return;
    }
    if (parseInt(cantidad) > dispGrupo) {
      toast.error(`Solo hay ${dispGrupo} disponibles de ${groupCode}`); return;
    }
    setSaving(true);
    try {
      const res = await API.post('/garantia', {
        warehouse_id: warehouseId, group_code: groupCode,
        cantidad: parseInt(cantidad), responsable, observaciones
      });
      toast.success(`Salida por garantía registrada: ${res.data.folio}`);
      setCantidad(''); setResponsable(''); setObservaciones(''); setGroupCode('');
      const [d, gar] = await Promise.all([
        API.get('/garantia/disponible', { params: { warehouse_id: warehouseId } }),
        API.get('/garantia')
      ]);
      setDisponible(d.data); setGarantias(gar.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Garantía</h1>
        <p className="text-sm text-muted-foreground">Salida definitiva de acumuladores por garantía del fabricante</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="nuevo">Nueva Salida</TabsTrigger>
          <TabsTrigger value="historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="nuevo" className="mt-4">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <Card className="bg-white border">
              <CardHeader className="pb-3"><CardTitle className="text-sm">Disponible por Grupo</CardTitle></CardHeader>
              <CardContent>
                <Select value={warehouseId} onValueChange={setWarehouseId}>
                  <SelectTrigger><SelectValue placeholder="Almacén" /></SelectTrigger>
                  <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.nombre}</SelectItem>)}</SelectContent>
                </Select>
                <div className="mt-3 space-y-1">
                  {disponible.map(d => (
                    <div key={d.group_code}
                      className={`flex justify-between text-sm p-2 rounded cursor-pointer transition-colors ${groupCode === d.group_code ? 'bg-[#0F6E87]/10 border border-[#0F6E87] font-medium' : 'hover:bg-muted/40 border border-transparent'}`}
                      onClick={() => setGroupCode(d.group_code)}>
                      <span>{d.group_code}</span>
                      <span className="font-semibold">{d.disponible} pzas</span>
                    </div>
                  ))}
                  {disponible.length === 0 && warehouseId && <p className="text-sm text-muted-foreground py-2">Sin inventario disponible</p>}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border xl:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Envío a Garantía</CardTitle>
                <p className="text-xs text-red-500">Esta salida es definitiva y descuenta inventario permanentemente.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Grupo *</label>
                    <Select value={groupCode} onValueChange={setGroupCode}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona grupo" /></SelectTrigger>
                      <SelectContent>
                        {disponible.map(d => (
                          <SelectItem key={d.group_code} value={d.group_code}>{d.group_code} — {d.disponible} pzas disponibles</SelectItem>
                        ))}
                        {disponible.length === 0 && <SelectItem value="_none" disabled>Sin inventario</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Cantidad *</label>
                    <Input type="number" min="1" max={dispGrupo} value={cantidad} onChange={(e) => setCantidad(e.target.value)} className="mt-1" />
                  </div>
                </div>
                {groupCode && (
                  <div className="p-3 rounded-lg bg-muted/50 flex items-center justify-between">
                    <span className="text-sm">Grupo seleccionado: <strong>{groupCode}</strong></span>
                    <span className="text-sm">Disponible: <strong>{dispGrupo} pzas</strong></span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Responsable *</label>
                    <Input value={responsable} onChange={(e) => setResponsable(e.target.value)} className="mt-1" placeholder="Nombre" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Observaciones</label>
                    <Input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} className="mt-1" placeholder="Opcional" />
                  </div>
                </div>
                <Button onClick={handleSave} disabled={saving || !groupCode || !warehouseId} className="bg-[#0F6E87] hover:bg-[#0C5D73]">
                  <ShieldCheck className="h-4 w-4 mr-1" /> {saving ? 'Registrando...' : 'Registrar Salida por Garantía'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="historial" className="mt-4">
          <Card className="bg-white border"><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="bg-muted/30">
                <TableHead>Folio</TableHead><TableHead>Grupo</TableHead><TableHead className="text-right">Cantidad</TableHead>
                <TableHead>Responsable</TableHead><TableHead>Observaciones</TableHead><TableHead>Fecha</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {garantias.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Sin salidas por garantía</TableCell></TableRow>}
                {garantias.map(g => (
                  <TableRow key={g.id} className="hover:bg-muted/40">
                    <TableCell className="font-mono text-xs">{g.folio}</TableCell>
                    <TableCell className="font-medium">{g.group_code}</TableCell>
                    <TableCell className="text-right font-semibold">{g.cantidad}</TableCell>
                    <TableCell>{g.responsable}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{g.observaciones || '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(g.fecha).toLocaleDateString('es-MX')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
